import { CommonModule } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, Component, forwardRef, Injector, Input, OnInit } from "@angular/core";
import { ControlValueAccessor, FormControl, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors } from "@angular/forms";
import { BiTranslateDirective, BiTranslatePipe, BiTranslateService } from "@globals/bi-translate";
import { CustomValidators } from "@globals/classes/CustomValidators";
import { BiCountryId } from "@globals/enums/BiLanguageAndCountryId";
import { isStringNullOrEmpty, uniqueID } from "@globals/helper-functions";
import { InputTextModule } from "primeng/inputtext";
import { take } from "rxjs";

/**
 * Custom input for entering a zip code. Validation logic and error message displaying is included!
 **/
@Component({
  selector: "bi-zip-code-input",
  imports: [CommonModule, InputTextModule, FormsModule, BiTranslatePipe, BiTranslateDirective],
  templateUrl: "./bi-zip-code-input.component.html",
  styles: [":host{display: block}"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BiZipCodeInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BiZipCodeInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export default class BiZipCodeInputComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  private _textValue: string;
  public get textValue() {
    return this._textValue;
  }
  public set textValue(val: string) {
    this._textValue = val;
  }

  public disabled: boolean;

  public readonly uniqueInputId = uniqueID(); // for setting a unique html id on the input

  /**
   * Label value that will be displayed inside the grey box being added in front of input.
   * If this is false or undfined, the box will not be shown - e.i. normal input style.
   * Default undefined.
   */
  @Input() public inputGroupLabel: string;

  /**
   * Label for the input. Default is translated value of "zipcode"
   */
  @Input() public label: string;

  /**
   * Whether the componernt will show a label for the input. Default is true
   */
  @Input() public hasLabel = true;

  @Input() set countryId(id: BiCountryId) {
    this.inputMaxLength = id !== BiCountryId.FI && id !== BiCountryId.SE ? 4 : 5;
  }

  @Input() public formControl: FormControl;

  /**
   * Get ref to FormControl
   **/
  public get control() {
    return this.formControl;
  }

  /**
   * Max. length allowed for input. As the zip code's length can defer in length depending on country
   */
  public inputMaxLength: number;

  private propagateChange: (val: string) => void;
  private onTouched: () => void;

  public constructor(
    private readonly injector: Injector,
    private readonly translator: BiTranslateService
  ) {}

  public ngOnInit() {
    if (!this.label) {
      this.translator
        .get("shared.Zipcode")
        .pipe(take(1))
        .subscribe(zipcodeText => (this.label = zipcodeText));
    }
  }

  public ngAfterViewInit() {
    // If client didn't use a [FormControl] binding, then we have to inject the ref. to it ourselves
    if (!this.formControl) this.formControl = this.injector.get(NgControl)?.control as FormControl;
  }

  public writeValue(val: string): void {
    // By using only double equal sign (==), we test against undefined and null
    if (val == this._textValue) return;

    this._textValue = val;
    if (this.propagateChange) this.propagateChange(val);
  }

  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  public onChanged(event: KeyboardEvent) {
    if (event.key === "Tab") return;

    this.propagateChange((event.target as HTMLInputElement).value);
    if (this.onTouched) this.onTouched();
  }

  /**
   * Customer valdiation being run on the control so that clients don't
   * have to manually add validators.
   */
  public validate(control: FormControl): ValidationErrors | null {
    if (isStringNullOrEmpty(control.value)) return { required: true };

    // In Sweden and Denmark, the zipcode MUST have an exact length
    const lengthErr = CustomValidators.checkLength(this.inputMaxLength)(control);
    if (lengthErr != null) return lengthErr;

    if (control.value.length > this.inputMaxLength) return { maxLength: true };

    // Last validation so just return it's result
    return CustomValidators.digitsOnly(false)(control);
  }
}
